/**
 * The centre panel (TMMAPP-0142 items 2–5, 7, 10, 16–18): the session's header (its name, the scope
 * as chips with Edit scope, and usage), the transcript, and the composer with Fast / Deep and the
 * disclaimer line.
 *
 * - The transcript is a log region. Each arrival is announced once, politely, through one live
 *   region (H7): "The assistant answered." for an answer, "{author}: new message" for another
 *   member's message; the log itself stays quiet so nothing is read twice. "Thinking…" is a polite
 *   status row while an answer runs. Focus never moves.
 * - Every AI answer is safe Markdown with the AI pill (TMM-B9); human messages carry no pill. No
 *   answer shows its cost (TMMAPP-0162 item 1): the served `chargedMinor` is read only into the
 *   privacy panel, the usage meter and AI history. A scope request is its own transcript line, the opt-in card (`SCOPE_REQUEST`, J2), and is
 *   announced like an answer.
 * - The composer stays usable while an answer runs, with asking held; a refusal shows at the
 *   composer (`409 ASSISTANT_TURN_RUNNING`, `503 ASSISTANT_BUSY`, an AI refusal through
 *   `AiWalletPrompt`). Only the served depths (`options.tiers`) are offered (H10).
 * - An archived session is read-only: "Archived. Restore to continue." replaces the composer. A
 *   sharer's session whose access ended reads "You no longer have assistant access…".
 *
 * **A shared session** (U3): a "Shared" tag and the members' avatars (the served `members[]`, J3; a
 * member who can't ask has the tooltip "Can't ask the assistant"); each message shows its author;
 * the composer has **Send to members** (free; Enter) and **Send to assistant** (Ctrl/Cmd+Enter), side
 * by side at the box's end (TMMAPP-0164 item 15). Each answer says who asked (`askedBy`), never what
 * it cost.
 * While another member's question runs (`runningTurn`), everyone sees "Thinking…" with "{name}
 * asked". Each member's own data shows to everyone as a banner; a member removes their own from
 * it, and adds it with "Add my accounts". The transcript is read for the person as it arrives
 * (`POST …/read`).
 *
 * **Who can ask** (TMMAPP-0164 items 13–14; TMMAPI-0195 U4, MM-D-149 amended): every ACTIVE share of
 * the virtual account, guests included, is a member, and the privacy line ends with **Who can join?**
 * (`WhoCanJoin`). Whether the caller can ask is their own member row's `canAsk` (the owner's grant,
 * their AI on, their limit not used up; on the owner's credit, with no subscription of their own), not
 * their tier: a guest the owner allowed asks without the purchase, and a subscriber the owner didn't
 * allow only messages. When they can't, Send to assistant stays and explains by `canAskReason`
 * (`CantAskNotice`; a guest not allowed is offered the purchase there), Fast / Deep is hidden, the
 * hint says asking needs the owner's permission, and the Private room tab is hidden unless they
 * already have one, which opens read-only with the explanation in place of the composer. A refused
 * ask (`403 ASSISTANT_ASK_NOT_ALLOWED {reason}`) shows the same explanation.
 *
 * **A guest** (GUEST or NONE tier, M11): reads and sends in the shared sessions they were invited
 * to; Add my accounts opens the purchase experience (never disabled).
 *
 * **Attachments** (U5, items 25a–25b): the composer attaches files (`useAttachments`), sent on the
 * message as `attachmentIds`; a swept upload (`404 ASSISTANT_ATTACHMENT_NOT_FOUND`) turns the chips
 * it names (`details.missing`) expired, or all of them when it names none (J5). Each message shows
 * its files (`MessageAttachments`), with the uploader in a shared session. A send whose answer was
 * lost (`409 ASSISTANT_ATTACHMENT_ATTACHED`: its files are already on a message) was accepted: the
 * composer clears its text and those chips, and the session view and what's new are re-read, so
 * the caller's own `runningTurn` is followed and Send can't ask (and charge) again (K3, M3). The
 * depth sent is always a served one: the chosen tier if `options.tiers` has it, else the first
 * served tier (K10).
 *
 * **Help & feedback** (U2, `kind: SUPPORT`): tmm-app's own label, its intro, and no scope, data
 * categories or private data ("This assistant can't see your financial data."). Asking follows the
 * assistant's rules (a subscriber, AI on, the wallet; a person without a subscription, or a
 * `403 SUBSCRIPTION_REQUIRED`, opens the purchase). A turn that drafted a ticket
 * (`NEEDS_CONFIRMATION`) shows the answer, then the draft card (`TicketDraftCard`). The draft is the
 * transcript's: every `ASSISTANT` line with a `ticketDraft` and no `ticketNumber` shows File it, so
 * an unfiled draft survives a reload, and one with a `ticketNumber` reads "Filed as {number}",
 * which opens the ticket (L8). A turn that drafted with no answer text still has its line (empty
 * text, TMMAPI-0185 U1): it renders as the draft card alone, with no empty answer. **Not now**
 * stores the choice (`POST …/ticket-draft/dismiss`), so a dismissed draft is omitted from its line
 * and doesn't come back; on a `409` (filed meanwhile) the line is read again, so it shows "Filed as
 * {number}". A blank line with no draft left shows nothing. An answer that cites a help article
 * links to it in the panel (only here: the page passes `onOpenArticle` for Help & feedback alone,
 * M5).
 *
 * **AI tools** (U7, L2): beside Edit scope, on every session but Help & feedback, the session's
 * connected AI tools (`onAgentTools`); an archived session's are shown read-only.
 *
 * **A private room** (TMMAPP-0148 U1, `visibility: BREAKOUT`; v2's breakout, TMMAPP-0153 items 4–10;
 * the room tabs, TMMAPP-0178 items 1–3, which replace Step into and Return): above the title row, on
 * every conversation but Help & feedback, the **Shared room** and **Private room** tabs (`RoomTabs`;
 * the chat below is their `tabpanel`). A shared conversation has both (Private room only where a room
 * can be opened: a subscriber's, who has one or can ask there; `onOpenRoom`); its private room has
 * both, Private room selected, while its shared session reads (Shared room: `onOpenShared`); a
 * private conversation, or a room whose shared session is gone, has Private room alone. Switching
 * never prompts. The room is headed "My private room", its callout says "Your private room beside
 * “{shared session}”" and who can't see it (the shared session's other members, `parent`), and it
 * asks like a private session (Send asks). "Add my accounts" opens the same editor, with no banner
 * here: only the person sees it. Its header keeps **Bring something back**, and a menu with Delete
 * (`onDelete`: the list no longer holds the room's row). An answer's `replyDraft` is a card with Copy
 * and Post to {shared session}; Post, like Bring something back, opens **Bring something back?**
 * (`BringBackDialog`) with that draft (Bring something back: the latest) in the note. Nothing posts
 * until the person presses Share & return, and then as their own message (`onShareBack`); Cancel
 * stays in the room. When the shared session is gone (`parentAvailable: false`) the room is
 * read-only: the callout says so, and the composer's place offers Delete (`onDelete`).
 *
 * **The v2 look** (TMMAPP-0153 items 4, 7, 8; visual contract v2 §11): the header is the title
 * (Newsreader 24), the privacy line in the kind's tone, "Paid from …" and the member's allowance
 * under it (`myMonthlyLimit`, item 16), then the people (30 px avatars, then the assistant's
 * sparkle), Bring something back in a private room, search and the panel button. Messages are bubbles: the person's on
 * the inline end in `brand`, the assistant's on `surface` with a `line` border and the AI mark,
 * another member's on the peach `soft`. "Thinking…" is three dots (static with reduced motion) with
 * the words for assistive technology. The composer offers the kind's prompt chips, which fill it.
 * The header is one row at `lg` (TMMAPP-0154 item 24): the title, the privacy line and "Paid from …"
 * and the allowance under it (13, `text3`) on the inline start; the people, Bring something back,
 * search, the panel button, the archived chip and **Session details** on the inline end. TMMAPP-0156
 * item 1: at `lg` and up that's one row whatever the title's length (the title column's lines
 * ellipsize; the panel button and Session details are icon buttons named by their tooltips); below
 * `lg` the actions wrap under the title column as one group. TMMAPP-0157 items 1–2: the lines
 * ellipsize at `lg` only (below it they wrap in full), and at `lg` the title column keeps 160 px,
 * so where that and the actions don't fit the actions wrap under it. At every
 * width the scope, usage and private-data rows open from Session details (`aria-expanded`), so the
 * transcript keeps the room and a short room's callout is on screen. Session details is the one place
 * for charges (TMMAPP-0178 items 8–9): the usage (`UsageBar`), then the note that pictures and PDFs
 * cost more to send than text, when a call in this conversation sent files (`filesSent`).
 *
 * **The member's allowance** (TMMAPI-0186 U1, item 17): `MEMBER_LIMIT` (a failed turn, or a send's
 * `409 AI_DISABLED {reason: MEMBER_LIMIT}`) reads "You've reached your monthly allowance for the
 * assistant on {virtual account}. {owner} can change it.", with no figure and no wallet prompt.
 *
 * **Who pays** (TMMAPI-0184 U3): an anchored session says "Paid from your AI credit" or "Paid from
 * {owner}'s AI credit" under its header (`paidBy`). A refusal of the owner's credit
 * (`PAYER_UNAVAILABLE`, a send's 409 or a failed turn) names them with no figure and no wallet
 * prompt; the person's own AI balance and Top up show only where they pay.
 *
 * **Paging and search** (TMMAPP-0148 U2 items 11–14): the transcript opens at its newest page.
 * Scrolling to the top (or "Show earlier messages") reads the page before it and keeps the reading
 * place, without moving focus; with none left it reads "Start of the conversation". The header's
 * search opens a field over the transcript (`TranscriptSearch`); opening a hit reads the window
 * around it, scrolls to it, highlights it briefly and moves focus to it, and "Back to the newest"
 * returns. The page's `?message=` deep link (`jumpTo`) opens the same way. Below a found line,
 * scrolling to the bottom (or "Show newer messages") reads the next newer page while `hasNewer`
 * (TMMAPI-0185 U1); once the newest is held, the live transcript carries on from it. Lines read
 * that way are no arrival.
 */
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  ButtonBase,
  Chip,
  IconButton,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { Trans, useTranslation } from 'react-i18next';
import { OverflowMenu } from '@/shell/OverflowMenu';
import type { AiWallet } from '@/api/aiWallet';
import {
  assistantApi,
  type AssistantMessage,
  type AssistantOptions,
  type AssistantPrivateData,
  type AssistantScopeRequest,
  type AssistantSession,
  type AssistantSessionMember,
  type AssistantAskReason,
  type AssistantTier,
  askRefusalReason,
  MEMBER_LIMIT,
} from '@/api/assistant';
import { errorCopy } from '@/api/errorCopy';
import { attachmentErrorCopy } from './composer/attachmentRules';
import { useAttachments } from './composer/useAttachments';
import { MessageAttachments } from './MessageAttachments';
import {
  aiRefusal,
  ErrorCodes,
  isAiWalletRefusal,
  isApiError,
  payerUnavailableReason,
  type ApiError,
} from '@/api/errors';
import { ticketLabel, type Ticket } from '@/api/tickets';
import { useOpenSubscribe } from '@/features/subscribe/subscribeOfferContext';
import { useMe } from '@/session/meHooks';
import { SubscriberActionHint, type SubscriberAction } from '@/session/useSubscriberAction';
import { useBrand } from '@/theme/useBrand';
import { TicketDraftCard, type DraftCard } from './support/TicketDraftCard';
import { AiPill } from '@/components/AiPill';
import { ErrorState } from '@/components/ErrorState';
import {
  AssistantSparkleNavIcon,
  BackIcon,
  CloseIcon,
  InfoIcon,
  SearchIcon,
} from '@/components/icons';
import { LiveRegion } from '@/components/LiveRegion';
import { MoneyText } from '@/components/MoneyText';
import { SafeMarkdown } from '@/components/SafeMarkdown';
import { SegmentedControl } from '@/components/SegmentedControl';
import { AiWalletPrompt } from '@/features/aiWallet/AiWalletPrompt';
import { formatList } from '@/i18n/format';
import { personName } from '@/i18n/personName';
import { useLocale } from '@/i18n/useLocale';
import { LazyRootContext } from '@/shared/lazyRootContext';
import { initialOf } from '@/shell/initials';
import { PILL_RADIUS } from '@/theme';
import { tintFor } from '@/theme/categorical';
import { resolveTokens } from '@/theme/theme';
import {
  memberLimitCopy,
  paidByCopy,
  payerUnavailableCopy,
  privateDataBanner,
} from './assistantCopy';
import { BringBackDialog } from './BringBackDialog';
import { CantAskNotice } from './CantAskNotice';
import { Composer } from './composer/Composer';
import { grantableScope } from './scopeGrant';
import { ReplyDraftCard } from './ReplyDraftCard';
import { RoomTabs, type RoomTab } from './RoomTabs';
import { ScopeRequestCard } from './ScopeRequestCard';
import { SessionKindIcon } from './SessionKindIcon';
import {
  otherMemberNames,
  privacyLineCopy,
  sessionKindInk,
  sessionKindOf,
  sessionVirtualAccountName,
  type SessionKind,
} from './sessionKind';
import { TurnFailure } from './TurnFailure';
import { UsageBar } from './UsageBar';
import { WhoCanJoin } from './WhoCanJoin';
import { useConversation } from './useConversation';
import { hitAuthor } from './search/hitAuthor';
import { SearchField, SearchHits } from './search/SearchResults';
import { lastHitId, useTranscriptSearch } from './search/useTranscriptSearch';

const ALL_TIERS: readonly AssistantTier[] = ['FAST', 'DEEP'];
/** The header's and the transcript's avatars (visual contract v2 §11). */
const AVATAR = 30;
/**
 * One line, ellipsized when it must be, at `lg` and up only: the chat header's title-column lines
 * (TMMAPP-0156 item 1). Below `lg` they wrap in full, so a phone never cuts the privacy line off
 * (TMMAPP-0157 item 1).
 */
const ONE_LINE = {
  whiteSpace: { lg: 'nowrap' },
  overflow: { lg: 'hidden' },
  textOverflow: { lg: 'ellipsis' },
} as const;

/** The ids a send's `404 ASSISTANT_ATTACHMENT_NOT_FOUND` names (`details.missing`); null when none. */
function missingAttachments(error: ApiError): number[] | null {
  const backend = error.details.backend;
  const missing = typeof backend === 'object' && backend !== null ? backend.missing : undefined;
  if (!Array.isArray(missing)) return null;
  const ids = missing.filter((id): id is number => typeof id === 'number');
  return ids.length > 0 ? ids : null;
}

/**
 * A breakout's shared session as read: its other members (the callout's names), whether it takes new
 * messages, and whether the person can ask there (their own member row, TMMAPP-0164 item 14).
 */
export interface RoomParent {
  others: string[];
  /** Its members, for the Shared room tab's avatars. */
  members: readonly AssistantSessionMember[];
  writable: boolean;
  canAsk: boolean;
  askReason: AssistantAskReason | null;
  ownerName: string | null;
}

interface ChatPanelProps {
  headingId: string;
  session: AssistantSession;
  options: AssistantOptions | null;
  wallet: AiWallet | null;
  onWallet: (wallet: AiWallet) => void;
  /** The person has AI on (a fallback when the session serves no member row for them). */
  aiAvailable?: boolean | undefined;
  /** A turn ended or stopped to ask: re-read the session, the privacy panel and the wallet. */
  onTurnEnd: (turnId: number) => void;
  /** Messages arrived after the first load: read what their answers cost. */
  onArrived?:
    ((arrived: readonly AssistantMessage[], all: readonly AssistantMessage[]) => void) | undefined;
  /** The session became read-only under us (archived, access ended): re-read it. */
  onSessionStale: () => void;
  onEditScope: () => void;
  onRestore: () => void;
  onAllowScope: (request: AssistantScopeRequest) => Promise<void>;
  /** "Add my accounts" (a shared session; a guest's opens the purchase). */
  onAddPrivateData?: SubscriberAction | undefined;
  /** A banner's Remove, for the person's own data. */
  onRemovePrivateData?: ((item: AssistantPrivateData) => void) | undefined;
  /** The person has read up to this message. */
  onRead?: ((upToId: number) => void) | undefined;
  /** Back to the list, where one pane shows at a time. */
  back?: ReactNode;
  /** The header's panel button (Privacy, or Help and tickets) where the panel isn't a column. */
  panelButton?: ReactNode;
  /** Help & feedback: an answer's link to a help article opens it in the panel. */
  onOpenArticle?: ((id: string) => void) | undefined;
  /** Help & feedback: a draft was filed (the panel's tickets re-read). */
  onTicketFiled?: ((ticket: Ticket) => void) | undefined;
  /** Help & feedback: opens a filed ticket in the panel. */
  onOpenTicket?: ((number: number) => void) | undefined;
  /** Opens the session's connected AI tools (never on Help & feedback). */
  onAgentTools?: (() => void) | undefined;
  /** A shared session's Private room tab: open (made the first time) the person's room on it. */
  onOpenRoom?: (() => void) | undefined;
  /** Opening the room is in flight. */
  openingRoom?: boolean | undefined;
  /** A private room's Shared room tab: go to its shared session. */
  onOpenShared?: (() => void) | undefined;
  /** A breakout: post the note as the person's own message to the shared session, then go there. */
  onShareBack?: ((note: string) => Promise<void>) | undefined;
  /**
   * A breakout's shared session as read: its other members (the callout's names) and whether it
   * takes new messages (Bring something back's Share); null while unknown.
   */
  parent?: RoomParent | null | undefined;
  /**
   * The shared session's read has settled for the room's current `parentAvailable`: until it has,
   * neither the read-only explanation nor Delete shows (TMMAPP-0166 item 1).
   */
  parentSettled?: boolean | undefined;
  /**
   * The shared session's read failed with anything but a 404: a read-only room shows the section
   * error with Retry, which reads it again (TMMAPP-0167 item 1). Null otherwise.
   */
  parentFailure?: { error: unknown; onRetry: () => void } | null | undefined;
  /** A private room: delete it (its header menu, or a read-only room's notice). */
  onDelete?: (() => void) | undefined;
  /** A call in this conversation sent files: Session details says they cost more to send. */
  filesSent?: boolean | undefined;
  /** Text the composer starts with (Ask about this). */
  initialText?: string | undefined;
  /**
   * The chat is on screen. Below md it stays mounted, hidden, while Help and tickets (or Privacy)
   * shows: nothing is marked read or announced until it shows again (TMMAPP-0148 E2).
   */
  visible?: boolean | undefined;
  /** Open a window around this line (a search hit across sessions, `?message=`); a new nonce reads it again. */
  jumpTo?: { messageId: number; nonce: number } | null | undefined;
}

/** How long a found line stays highlighted. */
const FOUND_HIGHLIGHT_MS = 2500;
/** Within this many pixels of the top, the transcript reads its earlier page. */
const OLDER_THRESHOLD_PX = 48;
/**
 * Session details' tooltip waits a moment on hover, as the sends' do (TMMAPP-0164 G18): a pointer on
 * its way to click doesn't open and position it on every click (TMMAPP-0165 item 19); a lingering
 * hover or focus still shows it.
 */
const DETAILS_TIP_DELAY_MS = 600;

/** What the person did with a draft in this view: filed (with the ticket) or set aside. */
type Settled = { outcome: 'filed'; label: string; number: number } | { outcome: 'dismissed' };

export function ChatPanel({
  headingId,
  session,
  options,
  wallet,
  onWallet,
  aiAvailable = false,
  onTurnEnd,
  onArrived,
  onSessionStale,
  onEditScope,
  onRestore,
  onAllowScope,
  onAddPrivateData,
  onRemovePrivateData,
  onRead,
  back,
  panelButton,
  onOpenArticle,
  onTicketFiled,
  onOpenTicket,
  onAgentTools,
  onOpenRoom,
  openingRoom = false,
  onOpenShared,
  onShareBack,
  parent = null,
  parentSettled = true,
  parentFailure = null,
  onDelete,
  filesSent = false,
  initialText,
  visible = true,
  jumpTo = null,
}: ChatPanelProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const theme = useTheme();
  const tokens = resolveTokens(theme);
  const mdUp = useMediaQuery(theme.breakpoints.up('md'));
  const me = useMe();
  const openSubscribe = useOpenSubscribe();
  // The header row is one row at `lg` and up; below it the actions wrap under the title column
  // (TMMAPP-0156 item 1, correcting TMMAPP-0155 item 2). At every width the scope, usage and
  // private-data rows open from Session details, so the transcript keeps the room.
  const [detailsOpen, setDetailsOpen] = useState(false);
  const showDetails = detailsOpen;
  const scopeRowId = useId();
  const usageRowId = useId();
  const bannersId = useId();
  const sharedTabId = useId();
  const privateTabId = useId();
  const panelId = useId();
  const { productName } = useBrand();
  const support = session.kind === 'SUPPORT';
  const shared = session.visibility === 'SHARED';
  const breakout = session.visibility === 'BREAKOUT';
  // A private room whose shared session no longer reads for the person: read-only (item 5).
  const roomClosed = breakout && !session.parentAvailable;
  const paidBy = paidByCopy(t, session);
  // A GUEST or NONE tier asks through the purchase experience (M11; Help & feedback too).
  const subscriber = me.tier === 'SUBSCRIBER';
  const readOnly = session.status === 'ARCHIVED' || session.accessEnded;
  const tiers = (options?.tiers ?? ALL_TIERS).filter(
    (value, index, all) => all.indexOf(value) === index,
  );
  const [tier, setTier] = useState<AssistantTier>(
    tiers.includes(session.defaultTier) ? session.defaultTier : (tiers[0] ?? session.defaultTier),
  );
  const [sendError, setSendError] = useState<unknown>(null);
  // Items 13–14: in a shared session the caller's own member row decides asking, not their tier.
  const myMember = shared ? (session.members.find((member) => member.mine) ?? null) : null;
  const callerCanAsk = !shared || (myMember !== null ? myMember.canAsk : aiAvailable && subscriber);
  const callerReason = myMember?.canAskReason ?? null;
  const virtualAccountName = sessionVirtualAccountName(session, me.virtualAccounts);
  const ownerName =
    session.paidBy !== null && !session.paidBy.mine
      ? session.paidBy.name
      : (session.members.find((member) => member.owner && !member.mine)?.name ??
        me.virtualAccounts.find((entry) => entry.id === session.virtualAccountId)
          ?.ownerDisplayName ??
        null);
  // Why the caller can't ask, shown at the composer (Send to assistant, or a refused ask).
  const [cantAsk, setCantAsk] = useState<{ reason: AssistantAskReason | null } | null>(null);
  // A re-read that restores asking closes the explanation by itself (TMMAPP-0166 item 3).
  const [couldAsk, setCouldAsk] = useState(callerCanAsk);
  if (couldAsk !== callerCanAsk) {
    setCouldAsk(callerCanAsk);
    if (callerCanAsk) setCantAsk(null);
  }
  // A breakout whose shared session reads but where the person can't ask: read-only (item 14).
  const roomCantAsk = breakout && !session.parentAvailable && parent !== null && !parent.canAsk;
  const [announcement, setAnnouncement] = useState({ key: 0, text: '' });
  const [settled, setSettled] = useState<Record<number, Settled>>({});
  // Bring something back? (item 9), open with its note.
  const [bringBack, setBringBack] = useState<{ note: string } | null>(null);
  // A deep link present when the chat opens: the first window is read around it.
  const [initialJump] = useState(jumpTo);
  const conversation = useConversation({
    sessionId: session.id,
    aroundId: initialJump?.messageId ?? null,
    shared,
    readOnly,
    onSessionStale,
    onTurnEnd,
    onAllowScope,
    onArrived,
    runningTurn: session.runningTurn,
  });
  const logRef = useRef<HTMLDivElement | null>(null);
  // The transcript's scroll area: its pictures load as it scrolls (M9).
  const [logNode, setLogNode] = useState<HTMLDivElement | null>(null);
  const attachLog = useCallback((node: HTMLDivElement | null) => {
    logRef.current = node;
    setLogNode(node);
  }, []);
  const { messages, turn } = conversation;
  // The session's 50 MB is the server's to check: the browser holds only the pages read (item 11).
  const attachments = useAttachments({ sessionId: session.id });
  const registry = options?.categories ?? null;
  const [searchOpen, setSearchOpen] = useState(false);
  const searchButton = useRef<HTMLButtonElement | null>(null);
  const searchInput = useRef<HTMLInputElement | null>(null);
  // Opening the search moves focus to its field.
  useEffect(() => {
    if (searchOpen) searchInput.current?.focus();
  }, [searchOpen]);
  const search = useTranscriptSearch(
    useCallback(
      (q: string, beforeId?: number) => assistantApi.searchMessages(session.id, q, beforeId),
      [session.id],
    ),
    lastHitId,
  );
  const { jump } = conversation;
  // A later deep link to this session (the chat already open) reads its window too.
  const lastJump = useRef(initialJump?.nonce ?? null);
  useEffect(() => {
    if (jumpTo === null || jumpTo.nonce === lastJump.current) return;
    lastJump.current = jumpTo.nonce;
    jump(jumpTo.messageId);
  }, [jumpTo, jump]);

  // Another member's question still being answered (J3): every member sees "Thinking…" until its
  // answer or its scope request arrives.
  const open = session.runningTurn;
  // Only its answer ends it (TMMAPP-0148 E5): a scope request line the asker already answered (a
  // continued turn) doesn't, while one still waiting on its asker does.
  const othersThinking =
    open !== null &&
    !open.mine &&
    !(messages ?? []).some(
      (message) =>
        message.turnId === open.turnId &&
        (message.role === 'ASSISTANT' ||
          (message.role === 'SCOPE_REQUEST' && message.outcome === 'PENDING')),
    );

  // New messages and rows keep the latest in view, without moving focus.
  const rows =
    (messages?.length ?? 0) +
    (turn === null ? 0 : 1) +
    (othersThinking ? 1 : 0) +
    Object.keys(conversation.turnDrafts).length;
  // Each window read lands at its newest (or, around a found line, on that line, highlighted and
  // focused); an earlier page keeps the reading place (item 11); Back to the newest focuses the log.
  const windowKey = conversation.window?.key ?? null;
  const around = conversation.window?.around ?? null;
  const firstId = messages?.[0]?.id ?? null;
  const olderLoading = conversation.older.loading;
  const olderHasMore = conversation.older.hasMore;
  const placed = useRef<number | null>(null);
  // The reader is at the newest line: new rows keep it there. Scrolled up into history, they don't.
  const atBottom = useRef(true);
  const keepPlace = useRef<{
    height: number;
    top: number;
    first: number | null;
    fromButton: boolean;
  } | null>(null);
  const focusLogOnNewest = useRef(false);
  const [found, setFound] = useState<number | null>(null);
  useLayoutEffect(() => {
    const node = logRef.current;
    if (node === null || windowKey === null) return;
    if (placed.current !== windowKey) {
      placed.current = windowKey;
      keepPlace.current = null;
      if (around !== null) {
        const line = node.querySelector<HTMLElement>(`[data-message="${around}"]`);
        if (line !== null) {
          if (typeof line.scrollIntoView === 'function') line.scrollIntoView({ block: 'center' });
          line.focus({ preventScroll: true });
          queueMicrotask(() => setFound(around));
        }
        return;
      }
      atBottom.current = true;
      node.scrollTop = node.scrollHeight;
      if (focusLogOnNewest.current) {
        focusLogOnNewest.current = false;
        node.focus();
      }
      return;
    }
    const place = keepPlace.current;
    if (place !== null) {
      if (firstId !== place.first) {
        keepPlace.current = null;
        node.scrollTop = place.top + (node.scrollHeight - place.height);
        // The button pressed is gone (nothing older remains): focus stays in the transcript.
        if (place.fromButton && !olderHasMore) node.focus({ preventScroll: true });
      } else if (!olderLoading) {
        // The read failed: nothing moved.
        keepPlace.current = null;
      }
      return;
    }
    if (around === null && atBottom.current) node.scrollTop = node.scrollHeight;
  }, [rows, windowKey, around, firstId, olderLoading, olderHasMore]);
  useEffect(() => {
    if (found === null) return undefined;
    const timer = window.setTimeout(() => setFound(null), FOUND_HIGHLIGHT_MS);
    return () => window.clearTimeout(timer);
  }, [found]);

  /** Reads the earlier page, keeping the reading place; focus stays where it is. */
  function loadOlder(fromButton = false) {
    const node = logRef.current;
    const { older } = conversation;
    if (node === null || messages === null || !older.hasMore || older.loading) return;
    keepPlace.current = {
      height: node.scrollHeight,
      top: node.scrollTop,
      first: firstId,
      fromButton,
    };
    conversation.loadOlder();
  }

  /** Reads the newer page below a found line; the reading place stays (it grows below). */
  function loadNewer() {
    const { newer } = conversation;
    if (messages === null || !newer.hasNewer || newer.loading) return;
    conversation.loadNewer();
  }

  // Each arrival is announced once (H7, item 17): an answer, or another member's message. While the
  // chat is hidden (below md, behind Help and tickets) it waits, and is announced when it shows
  // (TMMAPP-0148 E2). A window read (an earlier page, a found line, Back to the newest) is no
  // arrival.
  const newest = messages === null ? 'loading' : (messages.at(-1)?.id ?? 'none');
  const [seen, setSeen] = useState<{ window: number | null; newest: number | string }>({
    window: windowKey,
    newest,
  });
  if (visible && (newest !== seen.newest || windowKey !== seen.window)) {
    setSeen({ window: windowKey, newest });
    const before = seen.newest;
    if (windowKey === seen.window && before !== 'loading' && messages !== null) {
      const pagedTo = conversation.newer.readTo ?? 0;
      const arrived = messages.filter(
        (message) =>
          (typeof before !== 'number' || message.id > before) &&
          message.id > pagedTo &&
          !(message.role === 'USER' && message.mine),
      );
      const last = arrived.at(-1);
      if (last !== undefined) {
        setAnnouncement((current) => ({
          key: current.key + 1,
          text:
            last.role === 'ASSISTANT'
              ? t('assistant.answerReady')
              : last.role === 'SCOPE_REQUEST'
                ? t('assistant.scopeRequestReady')
                : t('assistant.shared.newMessage', { author: personName(last.authorName, t) }),
        }));
      }
    }
  }

  // What's in view is read (item 17): on open, as messages arrive, and when the tab is shown again.
  // Nothing is read while the chat itself is hidden; it is when the pane shows again (E2).
  const marked = useRef(0);
  const newestId = typeof newest === 'number' ? newest : null;
  const onReadRef = useRef(onRead);
  useEffect(() => {
    onReadRef.current = onRead;
  });
  useEffect(() => {
    if (newestId === null || !visible) return undefined;
    const mark = () => {
      if (document.visibilityState === 'hidden' || newestId <= marked.current) return;
      marked.current = newestId;
      assistantApi.markRead(session.id, newestId).then(
        () => onReadRef.current?.(newestId),
        () => {
          // Not recorded: the next arrival or visit tries again.
          marked.current = 0;
        },
      );
    };
    mark();
    document.addEventListener('visibilitychange', mark);
    return () => document.removeEventListener('visibilitychange', mark);
  }, [newestId, session.id, visible]);

  async function guarded(run: () => Promise<boolean>): Promise<boolean> {
    setSendError(null);
    setCantAsk(null);
    try {
      return await run();
    } catch (error) {
      // Its files are already on a message: the answer was lost, not the message (K3, M3). It was
      // accepted: the composer clears, and the session view (its `runningTurn`, followed when it is
      // the caller's) and what's new are read again.
      if (isApiError(error) && error.code === ErrorCodes.ASSISTANT_ATTACHMENT_ATTACHED) {
        onSessionStale();
        conversation.refresh();
        return true;
      }
      // Help & feedback: asking is a subscriber's (the purchase opens). An expired subscription is the
      // locked flow's, not the purchase's.
      if (support && isApiError(error) && error.code === ErrorCodes.SUBSCRIPTION_REQUIRED) {
        openSubscribe();
      }
      if (
        isApiError(error) &&
        (error.code === ErrorCodes.ASSISTANT_SESSION_ARCHIVED ||
          error.code === ErrorCodes.ASSISTANT_ACCESS_ENDED)
      ) {
        onSessionStale();
      }
      // The caller can't ask (item 14): the same explanation as Send to assistant's, never a generic
      // error, and the session is read again for its members' `canAsk`.
      if (isApiError(error) && error.code === ErrorCodes.ASSISTANT_ASK_NOT_ALLOWED) {
        setCantAsk({ reason: askRefusalReason(error) });
        onSessionStale();
        return false;
      }
      // The uploads were swept (24 h): the ones named say so, with re-attach (J5).
      if (isApiError(error) && error.code === ErrorCodes.ASSISTANT_ATTACHMENT_NOT_FOUND) {
        attachments.markExpired(missingAttachments(error));
      }
      setSendError(error);
      return false;
    }
  }

  // The depth sent is a served one: options may have arrived after the chat opened (K10).
  const servedTier = (): AssistantTier =>
    tiers.includes(tier) ? tier : (tiers[0] ?? session.defaultTier);
  const ask = async (text: string, ids: number[] = []) => {
    // A shared session or a breakout asks on the owner's credit: the member row decides, not a
    // subscription (TMMAPI-0195 U4). Elsewhere a person without one is offered it instead
    // (useSubscriberAction's rule).
    if (!subscriber && !shared && !breakout) {
      openSubscribe();
      return false;
    }
    atBottom.current = true;
    return guarded(() => conversation.send(text, servedTier(), ids));
  };
  const post = (text: string, ids: number[] = []) => {
    // The person's own message brings the newest back into view.
    atBottom.current = true;
    return guarded(() => conversation.post(text, ids));
  };

  function retryTurn(turnId: number) {
    const question = messages?.find(
      (message) => message.role === 'USER' && message.turnId === turnId,
    );
    if (question === undefined) return;
    conversation.dismissFailure();
    void ask(question.text);
  }

  const refusal = aiRefusal(sendError);
  const payerRefused = isApiError(sendError) && sendError.code === ErrorCodes.PAYER_UNAVAILABLE;
  const memberLimited = refusal?.reason === MEMBER_LIMIT;
  const notice =
    cantAsk !== null ? (
      <CantAskNotice
        reason={cantAsk.reason}
        virtualAccountName={virtualAccountName}
        ownerName={ownerName}
        onClose={() => setCantAsk(null)}
      />
    ) : sendError === null ? null : memberLimited ? (
      // The member's own allowance: no figure and no wallet prompt (item 17).
      <Alert severity="warning" data-send-error="" data-member-limit="">
        {memberLimitCopy(
          t,
          sessionVirtualAccountName(session, me.virtualAccounts),
          session.paidBy !== null && !session.paidBy.mine
            ? session.paidBy.name
            : (me.virtualAccounts.find((entry) => entry.id === session.virtualAccountId)
                ?.ownerDisplayName ?? null),
        )}
      </Alert>
    ) : payerRefused ? (
      // The owner's credit: no figure, no wallet prompt and no top-up for the person asking (item 8).
      <Alert severity="warning" data-send-error="" data-payer-unavailable="">
        {payerUnavailableCopy(t, session.paidBy?.name ?? null, payerUnavailableReason(sendError))}
      </Alert>
    ) : isAiWalletRefusal(refusal) ? (
      <AiWalletPrompt refusal={refusal} onTryAgain={() => setSendError(null)} />
    ) : (
      <Alert severity="warning" data-send-error="">
        {attachmentErrorCopy(t, sendError, locale, 'assistant.error.generic')}
      </Alert>
    );

  /**
   * A transcript line's box: focusable from a search hit (never in the tab order), and outlined
   * briefly once found (item 12).
   */
  const lineProps = (id: number, sx: Record<string, unknown> = {}) => ({
    'data-message': id,
    'data-found': found === id ? '' : undefined,
    tabIndex: -1,
    sx: {
      ...sx,
      ...(found === id
        ? { outline: `2px solid ${tokens.brandBoundary}`, outlineOffset: 4, borderRadius: 1 }
        : {}),
    },
  });
  function closeSearch() {
    setSearchOpen(false);
    searchButton.current?.focus();
  }
  const searchWho = (role: AssistantMessage['role']) => hitAuthor(t, role, shared);

  // The room's latest reply draft: Return's note starts with it (item 9).
  const latestReplyDraft =
    [...(messages ?? [])]
      .reverse()
      .find((message) => message.role === 'ASSISTANT' && message.replyDraft !== null)?.replyDraft ??
    null;
  const openBringBack = (note: string) => setBringBack({ note });

  const scopable = options?.scopable ?? [];
  const questionOf = (turnId: number | null) =>
    turnId === null
      ? undefined
      : messages?.find((message) => message.role === 'USER' && message.turnId === turnId);
  /** The person viewing asked this turn's question: only they choose on its card. */
  const askedByMe = (turnId: number | null): boolean => {
    const question = questionOf(turnId);
    if (question !== undefined) return question.mine;
    if (open !== null && open.turnId === turnId) return open.mine;
    return !shared;
  };
  const scopeLine = (message: AssistantMessage) => {
    const turnId = message.turnId;
    return (
      <Box key={message.id} {...lineProps(message.id)} data-role="scope-request">
        <ScopeRequestCard
          line={message}
          grant={
            message.scopeRequest === null
              ? null
              : grantableScope(session, options, message.scopeRequest)
          }
          mine={askedByMe(turnId)}
          choice={turnId === null ? undefined : conversation.choices[turnId]}
          scopable={scopable}
          registry={registry}
          readOnly={readOnly}
          onAllow={() => turnId !== null && void conversation.allow(turnId)}
          onDecline={() => turnId !== null && void conversation.decline(turnId)}
        />
      </Box>
    );
  };
  const placedDrafts = new Set(
    (messages ?? []).flatMap((message) =>
      message.role === 'USER' && message.turnId !== null ? [message.turnId] : [],
    ),
  );
  const answered = new Set(
    (messages ?? []).flatMap((message) =>
      message.role === 'ASSISTANT' && message.turnId !== null ? [message.turnId] : [],
    ),
  );
  /**
   * A support turn's ticket draft, with the files on the person's question: from the transcript's
   * line (`ticketDraft`, `ticketNumber`), else from the turn this view saw draft it (L8).
   */
  const draftFor = (turnId: number | null) => {
    if (turnId === null) return null;
    const line = messages?.find(
      (message) =>
        message.role === 'ASSISTANT' && message.turnId === turnId && message.ticketDraft !== null,
    );
    const seen = conversation.turnDrafts[turnId];
    const draft = line?.ticketDraft ?? seen?.draft ?? null;
    if (draft === null) return null;
    const done = settled[turnId];
    // Filed in this view (the ticket's own label), or before (the transcript's bare number,
    // labelled by the draft's type).
    const filedNumber = done?.outcome === 'filed' ? done.number : (line?.ticketNumber ?? null);
    const card: DraftCard = {
      turnId,
      draft,
      outcome:
        filedNumber !== null ? 'filed' : done?.outcome === 'dismissed' ? 'dismissed' : 'pending',
      filedAs:
        done?.outcome === 'filed'
          ? done.label
          : filedNumber !== null
            ? // The bare number: the draft's type may have changed before filing (E7).
              String(filedNumber)
            : null,
      filedNumber,
    };
    const question = messages?.find(
      (message) => message.role === 'USER' && message.turnId === turnId,
    );
    const words = line === undefined && !answered.has(turnId) ? (seen?.answer ?? null) : null;
    return (
      // A line with no text is the card alone: nothing above it to space from.
      <Box key={`draft-${turnId}`} sx={{ mt: line?.text.trim() === '' ? 0 : 1.5 }}>
        {words !== null && (
          <Box sx={{ mb: 1.5 }} data-draft-answer="">
            <Box sx={{ mb: 0.75 }}>
              <AiPill />
            </Box>
            <SafeMarkdown source={words} onArticle={onOpenArticle} />
          </Box>
        )}
        <TicketDraftCard
          sessionId={session.id}
          card={card}
          files={question?.attachments ?? []}
          readOnly={readOnly}
          onFiled={(ticket) => {
            setSettled((all) => ({
              ...all,
              [turnId]: { outcome: 'filed', label: ticketLabel(ticket), number: ticket.number },
            }));
            onTicketFiled?.(ticket);
          }}
          onDismiss={() => setSettled((all) => ({ ...all, [turnId]: { outcome: 'dismissed' } }))}
          onDismissRefused={(error) => {
            // Filed meanwhile: the line is read again, and shows "Filed as {number}". Any other
            // 409 means there's no draft left to dismiss: it reads as not filed.
            const backend = error.details.backend;
            const filed =
              typeof backend === 'object' && backend !== null && backend.reason === 'TICKET_FILED';
            if (!filed) setSettled((all) => ({ ...all, [turnId]: { outcome: 'dismissed' } }));
            // A failed re-read reads the conversation again, never silently (TMMAPP-0149 L1): its
            // own failure is the transcript's error, with Retry.
            if (line !== undefined) {
              void conversation.rereadLine(line.id).catch(() => conversation.reload());
            } else conversation.refresh();
          }}
          onOpenTicket={onOpenTicket}
        />
      </Box>
    );
  };

  const turnData = turn?.data ?? null;
  const kind = sessionKindOf(session);
  const memberLimit = memberLimitCopy(t, virtualAccountName, ownerName);
  const others = otherMemberNames(t, session);
  const label = tokens.textLabel('bg');
  const ring = { border: '2px solid', borderColor: tokens.bg, boxSizing: 'content-box' } as const;
  const peach = tintFor('peach', tokens);
  const avatarBase = {
    width: AVATAR,
    height: AVATAR,
    flexShrink: 0,
    fontSize: 12,
    fontWeight: 600,
  } as const;
  const assistantAvatar = (
    <Avatar
      aria-hidden
      data-assistant-avatar=""
      sx={{ ...avatarBase, bgcolor: tokens.successSoft, color: tokens.successText }}
    >
      <AssistantSparkleNavIcon size={16} />
    </Avatar>
  );
  const personAvatar = (name: string | null, mine: boolean) => (
    <Avatar
      aria-hidden
      sx={{
        ...avatarBase,
        bgcolor: mine ? tokens.brand : peach.soft,
        color: mine ? tokens.brandInk : tokens.text,
      }}
    >
      {initialOf(mine ? me.user.displayName : personName(name, t))}
    </Avatar>
  );

  /**
   * One transcript line in v2 (contract §11): a 30 px avatar beside a column of the author (12), the
   * bubble (up to min(560 px, 78 %), radius 18, padding 12 × 16) and what goes under it. The
   * person's own sit on the inline end.
   */
  function bubbleLine({
    id,
    role,
    end,
    avatar,
    author,
    bubble,
    below,
  }: {
    id: number;
    role: string;
    end: boolean;
    avatar: ReactNode;
    author: ReactNode;
    bubble: { sx: Record<string, unknown>; content: ReactNode } | null;
    below?: ReactNode;
  }) {
    return (
      <Box
        key={id}
        {...lineProps(id, {
          display: 'flex',
          gap: 1.25,
          flexDirection: end ? 'row-reverse' : 'row',
          alignItems: 'flex-start',
        })}
        data-role={role}
      >
        <Box sx={{ mt: '20px', display: 'flex' }}>{avatar}</Box>
        <Box
          sx={{
            maxWidth: 'min(560px, 78%)',
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            alignItems: end ? 'flex-end' : 'flex-start',
          }}
        >
          {author}
          {bubble !== null && (
            <Box
              data-bubble=""
              sx={{
                p: '12px 16px',
                borderRadius: '18px',
                fontSize: 15,
                lineHeight: 1.45,
                maxWidth: '100%',
                overflowWrap: 'anywhere',
                ...bubble.sx,
              }}
            >
              {bubble.content}
            </Box>
          )}
          {below}
        </Box>
      </Box>
    );
  }

  /**
   * An answer line. One with no text (a turn that only drafted a ticket, TMMAPI-0185 U1) is its
   * draft card alone; with no draft left either, it shows nothing.
   */
  function assistantLine(message: AssistantMessage) {
    const blank = message.text.trim() === '';
    const draft = draftFor(message.turnId);
    if (blank && draft === null && message.replyDraft === null) return null;
    const replyDraft =
      message.replyDraft !== null ? (
        <ReplyDraftCard
          messageId={message.id}
          text={message.replyDraft}
          sharedName={roomClosed ? null : session.parentName}
          onPost={roomClosed || onShareBack === undefined ? undefined : openBringBack}
        />
      ) : null;
    if (blank) {
      return (
        <Box
          key={message.id}
          {...lineProps(message.id, { maxWidth: 'min(640px, 100%)', marginInlineStart: '40px' })}
          data-role="assistant"
          data-draft-only=""
        >
          <Box>
            {draft}
            {replyDraft}
          </Box>
        </Box>
      );
    }
    return bubbleLine({
      id: message.id,
      role: 'assistant',
      end: false,
      avatar: assistantAvatar,
      author: (
        <Stack direction="row" sx={{ alignItems: 'center', gap: 0.75 }}>
          <Typography component="span" sx={{ fontSize: 12, color: label }} data-author="">
            {t('assistant.assistantAuthor')}
          </Typography>
          <AiPill />
        </Stack>
      ),
      bubble: {
        sx: {
          bgcolor: tokens.surface,
          color: tokens.text,
          border: `1px solid ${tokens.line}`,
          '& > :first-child': { mt: 0 },
          '& > :last-child': { mb: 0 },
        },
        content: <SafeMarkdown source={message.text} onArticle={onOpenArticle} />,
      },
      below: (
        <>
          {answerFooter(message)}
          {draft}
          {replyDraft}
        </>
      ),
    });
  }

  function humanMessage(message: AssistantMessage) {
    const author = message.mine ? t('assistant.you') : personName(message.authorName, t);
    return bubbleLine({
      id: message.id,
      role: 'user',
      end: message.mine,
      avatar: personAvatar(message.authorName, message.mine),
      author: (
        <Typography component="span" sx={{ fontSize: 12, color: label }} data-author="">
          {author}
        </Typography>
      ),
      bubble: {
        sx: message.mine
          ? { bgcolor: tokens.brand, color: tokens.brandInk }
          : { bgcolor: peach.soft, color: tokens.text },
        content: (
          <Typography
            component="p"
            sx={{ m: 0, fontSize: 15, lineHeight: 1.45, whiteSpace: 'pre-wrap', color: 'inherit' }}
          >
            {message.text}
          </Typography>
        ),
      },
      below: (
        <>
          <MessageAttachments
            sessionId={session.id}
            attachments={message.attachments}
            shared={shared}
          />
          {message.turnId !== null && !answered.has(message.turnId) && draftFor(message.turnId)}
        </>
      ),
    });
  }

  /**
   * In a shared session, who asked the answer. Its served charge (`chargedMinor`) is never shown
   * under it (TMMAPP-0162 item 1): the privacy panel, the usage meter and AI history carry the money.
   */
  function answerFooter(message: AssistantMessage) {
    if (!shared || message.askedBy === null) return null;
    return (
      <Typography component="p" sx={{ fontSize: 12, color: label, m: 0 }} data-asked-by="">
        {message.askedBy.mine
          ? t('assistant.shared.askedByYou')
          : t('assistant.shared.askedBy', { name: personName(message.askedBy.name, t) })}
      </Typography>
    );
  }

  const members = session.members;
  const memberNames = members.map((member) =>
    member.mine ? t('assistant.you') : personName(member.name, t),
  );
  // Who pays stays said (item 15): the members' messages are free; asking is the owner's to allow.
  const composerHint = shared ? (
    <Typography sx={{ fontSize: 12, color: label }} data-composer-hint="">
      {!callerCanAsk
        ? callerReason === 'AI_OFF'
          ? t('assistant.composer.noAi')
          : // The month's limit is used: no permission is missing (TMMAPP-0166 item 3).
            callerReason === 'LIMIT_REACHED'
            ? t('assistant.composer.limitReached')
            : ownerName !== null && ownerName.trim().length > 0
              ? t('assistant.composer.needsPermission', { owner: ownerName })
              : t('assistant.composer.needsPermissionUnnamed')
        : paidBy !== null
          ? // Who pays is said under the header (paidBy); the members' messages are free.
            t('assistant.composer.free')
          : t('assistant.composer.paid')}
    </Typography>
  ) : undefined;
  // Fast / Deep applies only to asking: hidden where the caller can't ask.
  const tierControl =
    tiers.length > 1 && callerCanAsk ? (
      <SegmentedControl
        aria-label={t('assistant.composer.tier')}
        value={tier}
        options={tiers.map((value) => ({
          value,
          label: value === 'DEEP' ? t('assistant.composer.deep') : t('assistant.composer.fast'),
        }))}
        onChange={setTier}
      />
    ) : undefined;
  // The session kind's prompt chips (TMMAPP-0153 item 8, from the prototype's): each fills the box.
  const chips = CHIPS[kind].map((key) => t(key));
  const placeholder = support
    ? t('support.placeholder')
    : shared
      ? others.length > 0
        ? t('assistant.composer.placeholderSharedNames', {
            people: formatList([...others, t('assistant.privacyLine.yourAssistant')], locale),
          })
        : t('assistant.composer.placeholderShared')
      : t('assistant.composer.placeholder');

  // The people: a shared session's served members (one initial each), else the person; then the
  // assistant (contract §11).
  const people =
    shared && memberNames.length > 0 ? (
      <Stack
        direction="row"
        role="group"
        // Who can't ask is in the group's name, not only in a hover tooltip (E3).
        aria-label={t('assistant.shared.members', {
          names: formatList(
            members.map((member, index) =>
              member.canAsk
                ? memberNames[index]!
                : t('assistant.shared.memberCantAsk', { name: memberNames[index] }),
            ),
            locale,
          ),
        })}
        sx={{ alignItems: 'center' }}
        data-members=""
      >
        {members.map((member, index) => (
          <Tooltip
            key={`${member.name ?? ''}-${index}`}
            title={
              member.canAsk ? (
                memberNames[index]
              ) : (
                <>
                  {memberNames[index]}
                  <br />
                  {t('assistant.shared.cantAsk')}
                </>
              )
            }
          >
            <Avatar
              aria-hidden
              sx={{
                ...avatarBase,
                ...ring,
                marginInlineStart: index === 0 ? 0 : '-6px',
                bgcolor: member.mine ? tokens.brand : peach.soft,
                color: member.mine ? tokens.brandInk : tokens.text,
              }}
              data-member=""
              data-can-ask={member.canAsk ? 'true' : 'false'}
            >
              {initialOf(member.mine ? me.user.displayName : personName(member.name, t))}
            </Avatar>
          </Tooltip>
        ))}
        <Box
          component="span"
          role="img"
          aria-label={t('assistant.assistantAvatar')}
          sx={{ display: 'flex', marginInlineStart: '-6px', borderRadius: '50%', ...ring }}
        >
          {assistantAvatar}
        </Box>
      </Stack>
    ) : (
      <Stack
        direction="row"
        role="group"
        aria-label={t('assistant.people', {
          names: formatList([t('assistant.you'), t('assistant.assistantAvatar')], locale),
        })}
        sx={{ alignItems: 'center' }}
        data-people=""
      >
        <Box sx={{ display: 'flex', borderRadius: '50%', ...ring }}>{personAvatar(null, true)}</Box>
        <Box sx={{ display: 'flex', marginInlineStart: '-6px', borderRadius: '50%', ...ring }}>
          {assistantAvatar}
        </Box>
      </Stack>
    );

  const pill = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 1,
    p: '9px 14px',
    borderRadius: `${PILL_RADIUS}px`,
    fontSize: 13,
    lineHeight: '18px',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    '&.Mui-focusVisible': { outline: `2px solid ${tokens.focusRing}`, outlineOffset: 2 },
  } as const;
  // The room tabs (TMMAPP-0178 items 1–2), on every conversation but Help & feedback. A shared
  // conversation's Private room is a subscriber's, where Step into showed (0148 item 6a): not where a
  // room can't be made (an archived or ended shared session with none yet), and a member who can't
  // ask gets no new room, while one they have opens read-only (item 14).
  const myself = { name: null, mine: true } as const;
  const sharedTab = (selected: boolean, members: readonly AssistantSessionMember[]): RoomTab => ({
    key: 'shared',
    id: sharedTabId,
    selected,
    people: members.map((member) => ({ name: member.name, mine: member.mine })),
    onOpen: () => onOpenShared?.(),
  });
  const privateTab = (selected: boolean): RoomTab => ({
    key: 'private',
    id: privateTabId,
    selected,
    people: [myself],
    onOpen: () => onOpenRoom?.(),
    busy: openingRoom,
  });
  // A room is "back" to its shared session while that reads (`parentAvailable`).
  const returnable = breakout && session.parentAvailable && session.parentSessionId !== null;
  const roomTabs: RoomTab[] = support
    ? []
    : shared
      ? [
          sharedTab(true, session.members),
          ...(subscriber &&
          onOpenRoom !== undefined &&
          (session.myBreakoutId !== null || (!readOnly && callerCanAsk))
            ? [privateTab(false)]
            : []),
        ]
      : returnable && onOpenShared !== undefined
        ? [sharedTab(false, parent?.members ?? []), privateTab(true)]
        : [privateTab(true)];
  const selectedTabId = roomTabs.find((tab) => tab.selected)?.id;
  // Bring something back (item 3), from a room whose shared session reads.
  const returnButton = returnable ? (
    <ButtonBase
      onClick={() => openBringBack(latestReplyDraft ?? '')}
      data-bring-back-open=""
      sx={{
        ...pill,
        bgcolor: tokens.brand,
        color: tokens.brandInk,
        '&:hover': { bgcolor: tokens.brandHover },
      }}
    >
      <Box component="span" sx={{ display: 'flex' }}>
        <BackIcon />
      </Box>
      {t('assistant.breakout.bringBack')}
    </ButtonBase>
  ) : null;

  // A shared session's private-data banners: behind Session details too. A private room's own data
  // is the person's alone: no banner there (item 3).
  const showBanners = !breakout && session.privateData.length > 0;
  // Search, the panel button, the archived chip and Session details end the title row at every
  // width (TMMAPP-0154 item 24).
  const tools = (
    <Stack
      direction="row"
      sx={{ alignItems: 'center', gap: 1, marginInlineStart: 'auto' }}
      data-chat-tools=""
    >
      {session.status === 'ARCHIVED' && <Chip size="small" label={t('assistant.archivedChip')} />}
      <IconButton
        ref={searchButton}
        size="small"
        aria-label={t('assistant.search.open')}
        aria-expanded={searchOpen}
        onClick={() => setSearchOpen((open) => !open)}
        data-search-open=""
      >
        <SearchIcon />
      </IconButton>
      {panelButton}
      {/* An icon button named by its tooltip, so the header stays one row (TMMAPP-0156 item 1). */}
      <Tooltip title={t('assistant.details')} enterDelay={DETAILS_TIP_DELAY_MS}>
        <IconButton
          size="small"
          aria-label={t('assistant.details')}
          aria-expanded={detailsOpen}
          aria-controls={
            support
              ? usageRowId
              : [scopeRowId, usageRowId, ...(showBanners ? [bannersId] : [])].join(' ')
          }
          onClick={() => setDetailsOpen((open) => !open)}
          data-session-details=""
          sx={detailsOpen ? { bgcolor: tokens.hover } : undefined}
        >
          <InfoIcon />
        </IconButton>
      </Tooltip>
      {/* A private room's Delete (item 4): the list no longer holds its row while its shared
          session reads, so its menu is here. */}
      {returnable && session.canManage && onDelete !== undefined && (
        <OverflowMenu title={t('assistant.breakout.privateRoom')}>
          {(close) => [
            <MenuItem
              key="delete"
              onClick={() => {
                close();
                onDelete();
              }}
            >
              {t('assistant.menu.delete')}
            </MenuItem>,
          ]}
        </OverflowMenu>
      )}
    </Stack>
  );
  const sharedName = session.parentName ?? session.name;
  const privacyLine = privacyLineCopy(t, session, locale);
  const parentOthers = parent?.others ?? [];
  const tabbed = roomTabs.length > 0;
  const backRow =
    back !== undefined ? (
      <Box sx={{ mb: 1 }} data-chat-actions="">
        {back}
      </Box>
    ) : null;

  return (
    <Stack sx={{ height: '100%', minHeight: 0, bgcolor: tokens.bg }}>
      {/* The room tabs sit at the top of the chat pane, above the title row (item 1); Back to the
          list, where one pane shows, stays first. */}
      {tabbed && (
        <Box sx={{ px: { xs: 2, md: 3.5 }, pt: 1.75, flexShrink: 0 }} data-room-tab-bar="">
          {backRow}
          <RoomTabs name={sharedName} tabs={roomTabs} panelId={panelId} />
        </Box>
      )}
      {/* The chat is the tabs' panel. */}
      <Stack
        id={panelId}
        role={tabbed ? 'tabpanel' : undefined}
        aria-labelledby={tabbed ? selectedTabId : undefined}
        sx={{ flex: 1, minHeight: 0 }}
      >
        <Box
          sx={{
            px: { xs: 2, md: 3.5 },
            pt: tabbed ? 1.5 : 2.25,
            pb: 2.25,
            borderBottom: `1px solid ${tokens.line}`,
            flexShrink: 0,
            maxHeight: '50%',
            overflowY: 'auto',
          }}
        >
          {!tabbed && backRow}
          {/* One row at `lg` and up (TMMAPP-0156 item 1, prototype state 10): the title column
            gives way and ellipsizes, the actions keep their width. Below `lg` the title column
            keeps its content's width (at least 280, at most the row) and the actions wrap under
            it as one group. At `lg` it keeps at least 160, so a long Bring something back pill and the archived
            chip can't squeeze the title away; where 160 and the actions don't fit (longer copy at
            1200), the actions wrap under it rather than overflow the row (TMMAPP-0157 item 2). */}
          <Stack
            direction="row"
            data-chat-header-row=""
            sx={{ alignItems: 'center', gap: 1.75, flexWrap: 'wrap' }}
          >
            <Box
              data-chat-title=""
              sx={{
                flex: { xs: '1 0 auto', lg: '1 1 0' },
                minWidth: { xs: 280, lg: 160 },
                maxWidth: '100%',
              }}
            >
              <Typography
                id={headingId}
                tabIndex={-1}
                component="h2"
                sx={{
                  m: 0,
                  fontFamily: (current) => current.typography.h4.fontFamily,
                  fontWeight: 400,
                  fontSize: 24,
                  lineHeight: 1.15,
                  color: tokens.text,
                  ...ONE_LINE,
                }}
              >
                {support
                  ? t('support.label')
                  : breakout
                    ? t('assistant.breakout.privateRoom')
                    : session.name}
              </Typography>
              <Box
                data-privacy-line={kind}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  mt: 0.5,
                  fontSize: 13,
                  lineHeight: '18px',
                  color: sessionKindInk(kind, tokens),
                }}
              >
                <Box component="span" sx={{ display: 'flex', flexShrink: 0 }} data-privacy-icon="">
                  {/* A breakout's privacy line is private: the lock (TMMAPP-0154 item 26). Its list tag
                    keeps the branch. */}
                  <SessionKindIcon kind={kind === 'breakout' ? 'private' : kind} />
                </Box>
                {/* Ellipsized at `lg`: the full sentence stays the span's text and is its tooltip
                  (TMMAPP-0157 item 1). */}
                <Tooltip describeChild title={privacyLine}>
                  <Box component="span" sx={{ minWidth: 0, ...ONE_LINE }} data-privacy-text="">
                    {privacyLine}
                  </Box>
                </Tooltip>
                {/* Item 13: who is in it, and who can ask. */}
                {shared && (
                  <WhoCanJoin
                    session={session}
                    virtualAccountName={virtualAccountName}
                    ownerName={ownerName}
                    onSharingChanged={onSessionStale}
                  />
                )}
              </Box>
              {/* "Paid from …" and the allowance sit under the privacy line, 13 in `text3` (item 24). */}
              {paidBy !== null && (
                <Typography
                  component="p"
                  sx={{
                    fontSize: 13,
                    lineHeight: '18px',
                    color: label,
                    mt: 0.5,
                    mb: 0,
                    ...ONE_LINE,
                  }}
                  data-paid-by=""
                >
                  {paidBy}
                </Typography>
              )}
              {session.myMonthlyLimit !== null && (
                <Typography
                  component="p"
                  sx={{
                    fontSize: 13,
                    lineHeight: '18px',
                    color: label,
                    mt: 0.25,
                    mb: 0,
                    fontVariantNumeric: 'tabular-nums',
                    ...ONE_LINE,
                  }}
                  data-allowance=""
                >
                  <Trans
                    i18nKey="assistant.allowance"
                    components={{
                      used: (
                        <MoneyText
                          minor={session.myMonthlyLimit.usedMinor}
                          currency={session.currency}
                          variant="caption"
                        />
                      ),
                      limit: (
                        <MoneyText
                          minor={session.myMonthlyLimit.limitMinor}
                          currency={session.currency}
                          variant="caption"
                        />
                      ),
                    }}
                  />
                </Typography>
              )}
            </Box>
            <Stack
              direction="row"
              data-chat-header-actions=""
              sx={{
                alignItems: 'center',
                gap: 1.75,
                flexShrink: 0,
                flexWrap: { xs: 'wrap', lg: 'nowrap' },
                marginInlineStart: 'auto',
                maxWidth: '100%',
              }}
            >
              {/* Below `md` the room tabs already show the same people, so the header drops its row
                  to leave the transcript room (TMMAPP-0177 item 6, the Operator). */}
              {(mdUp || !tabbed) && people}
              {returnButton}
              {tools}
            </Stack>
          </Stack>
          {support && (
            <Stack sx={{ gap: 0.5, mt: 1.25 }} data-support-intro="">
              <Typography sx={{ fontSize: 13, color: tokens.text }}>
                {t('support.intro', { productName })}
              </Typography>
              <Typography sx={{ fontSize: 12, color: label }} data-no-financial-data="">
                {t('support.noFinancialData')}
              </Typography>
            </Stack>
          )}
          {!support && (
            <Stack
              direction="row"
              id={scopeRowId}
              sx={{
                display: showDetails ? 'flex' : 'none',
                alignItems: 'center',
                gap: 0.75,
                flexWrap: 'wrap',
                mt: 1,
              }}
              data-scope-chips=""
            >
              <Typography component="span" sx={{ fontSize: 12, color: label }}>
                {t('assistant.scopeChips')}
              </Typography>
              {session.scope.length === 0 && (
                <Typography component="span" sx={{ fontSize: 13 }}>
                  {t('assistant.scopeSummaryEmpty')}
                </Typography>
              )}
              {session.scope.map((item) => (
                <Chip
                  key={`${item.kind}:${item.id}`}
                  size="small"
                  label={
                    item.available && item.name !== null
                      ? item.name
                      : item.name !== null
                        ? t('assistant.unavailableEntity', { name: item.name })
                        : t('assistant.unavailableUnnamed')
                  }
                />
              ))}
              {!readOnly && options !== null && session.canManage && (
                <Button size="small" onClick={onEditScope}>
                  {t('assistant.editScope')}
                </Button>
              )}
              {onAgentTools !== undefined && (
                <Button size="small" onClick={onAgentTools} data-agent-tools-open="">
                  {t('agentTools.sessionControl')}
                </Button>
              )}
              {(shared || breakout) &&
                !readOnly &&
                (options !== null || !subscriber) &&
                onAddPrivateData && (
                  <>
                    {/* A guest's opens the purchase, and says so (TMMAPP-0148 E4). */}
                    <Button
                      size="small"
                      onClick={onAddPrivateData.run}
                      aria-describedby={onAddPrivateData.descriptionId}
                    >
                      {t('assistant.shared.addMine')}
                    </Button>
                    <SubscriberActionHint
                      id={onAddPrivateData.descriptionId}
                      description={onAddPrivateData.description}
                    />
                  </>
                )}
            </Stack>
          )}
          {/* A private room's own data is the person's alone: no banner there (item 3). */}
          {showBanners && (
            <Stack
              id={bannersId}
              sx={{ gap: 0.75, mt: 1, display: showDetails ? 'flex' : 'none' }}
              data-private-banners=""
            >
              {session.privateData.map((item, index) => (
                <Alert
                  key={`${item.kind}:${item.accountId ?? item.category ?? ''}:${index}`}
                  severity="info"
                  data-private-banner=""
                  sx={{ py: 0 }}
                  action={
                    item.mine && onRemovePrivateData !== undefined ? (
                      <Button
                        color="inherit"
                        size="small"
                        onClick={() => onRemovePrivateData(item)}
                      >
                        {t('assistant.shared.remove')}
                      </Button>
                    ) : undefined
                  }
                >
                  {privateDataBanner(t, item, registry)}
                </Alert>
              ))}
            </Stack>
          )}
          <Box id={usageRowId} sx={{ display: showDetails ? 'block' : 'none', mt: 0.75 }}>
            <UsageBar
              session={session}
              wallet={wallet}
              onWallet={onWallet}
              // The person's own balance and Top up show only where they pay (TMMAPI-0184 U3).
              showBalance={session.paidBy === null || session.paidBy.mine}
            />
            {/* Charges live here only (TMMAPP-0178 item 8): the files note, when files were sent. */}
            {filesSent && (
              <Typography
                sx={{ fontSize: 12, lineHeight: '16px', color: label, mt: 0.75 }}
                data-files-note=""
              >
                {t('assistant.privacy.filesCost')}
              </Typography>
            )}
          </Box>
        </Box>

        {searchOpen && (
          <Box
            sx={{
              px: { xs: 2, md: 3.5 },
              py: 1.5,
              borderBottom: `1px solid ${tokens.line}`,
              maxHeight: '45%',
              overflowY: 'auto',
              flexShrink: 0,
            }}
            data-transcript-search=""
          >
            <SearchField
              search={search}
              label={t('assistant.search.field')}
              inputRef={searchInput}
              onEscape={closeSearch}
              end={
                <IconButton aria-label={t('assistant.search.close')} onClick={closeSearch}>
                  <CloseIcon />
                </IconButton>
              }
            />
            {search.q !== '' && (
              <SearchHits
                search={search}
                groups={[{ key: 'session', label: null, hits: search.hits ?? [] }]}
                whoOf={(hit) => searchWho(hit.role)}
                onOpen={(hit) => {
                  setSearchOpen(false);
                  jump(hit.id);
                }}
              />
            )}
          </Box>
        )}
        {conversation.awayRequested && (
          <Box
            sx={{
              px: { xs: 2, md: 3.5 },
              py: 0.75,
              borderBottom: `1px solid ${tokens.line}`,
              flexShrink: 0,
            }}
            data-away-from-newest=""
          >
            <Button
              size="small"
              onClick={() => {
                focusLogOnNewest.current = true;
                conversation.toNewest();
              }}
            >
              {t('assistant.history.backToNewest')}
            </Button>
          </Box>
        )}
        <Box
          ref={attachLog}
          onScroll={(event) => {
            const node = event.currentTarget;
            atBottom.current =
              node.scrollHeight - node.scrollTop - node.clientHeight <= OLDER_THRESHOLD_PX;
            if (node.scrollTop <= OLDER_THRESHOLD_PX) loadOlder();
            if (atBottom.current) loadNewer();
          }}
          role="log"
          aria-live="off"
          // A scrollable region the keyboard can reach and scroll (it may hold no control).
          tabIndex={0}
          aria-label={t('assistant.transcript')}
          data-transcript=""
          sx={{
            flex: 1,
            minHeight: 160,
            overflowY: 'auto',
            px: { xs: 2, md: 3.5 },
            py: 3,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <LazyRootContext.Provider value={logNode}>
            {breakout && (
              // The room's callout (item 6): what it is, or that its shared session is gone (item 5).
              <Box
                data-breakout-callout={roomClosed ? 'closed' : ''}
                sx={{
                  border: '1px dashed',
                  borderColor: tokens.brandBoundary,
                  bgcolor: tokens.brandSoft,
                  color: tokens.text,
                  borderRadius: '16px',
                  p: '14px 18px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.75,
                  maxWidth: 640,
                  flexShrink: 0,
                }}
              >
                {roomClosed ? (
                  <Typography sx={{ fontSize: 14, color: tokens.text }}>
                    {t('assistant.breakout.unavailable', { parentName: sharedName })}
                  </Typography>
                ) : (
                  <>
                    <Typography
                      component="h3"
                      sx={{
                        m: 0,
                        fontSize: 12,
                        fontWeight: 600,
                        letterSpacing: '0.05em',
                        textTransform: 'uppercase',
                        color: tokens.text,
                      }}
                    >
                      {t('assistant.breakout.calloutTitle', { sharedName })}
                    </Typography>
                    <Typography sx={{ fontSize: 12.5, color: tokens.text }}>
                      {parentOthers.length > 0
                        ? t('assistant.breakout.calloutBody', {
                            names: formatList(parentOthers, locale),
                          })
                        : t('assistant.breakout.calloutBodyUnnamed')}
                    </Typography>
                  </>
                )}
              </Box>
            )}
            {conversation.loadError !== null && (
              <ErrorState
                error={conversation.loadError}
                what={t('assistant.loadSessionError')}
                onRetry={conversation.reload}
              />
            )}
            {messages === null && conversation.loadError === null && (
              <Typography sx={{ fontSize: 14, color: tokens.text2 }}>
                {t('common.loading')}
              </Typography>
            )}
            {messages !== null &&
              messages.length > 0 &&
              (conversation.older.hasMore ? (
                <Box data-older-lines="">
                  {/* Stays mounted while it reads, so focus never moves (item 11). */}
                  <Button size="small" onClick={() => loadOlder(true)} aria-busy={olderLoading}>
                    {olderLoading
                      ? t('assistant.history.loadingEarlier')
                      : t('assistant.history.earlier')}
                  </Button>
                  {conversation.older.error !== null && (
                    <Alert severity="warning" sx={{ mt: 1 }} data-older-failed="">
                      {errorCopy(t, conversation.older.error, 'assistant.history.earlierError')}
                    </Alert>
                  )}
                </Box>
              ) : (
                <Typography
                  sx={{ fontSize: 12, color: label, textAlign: 'center' }}
                  data-transcript-start=""
                >
                  {t('assistant.history.start')}
                </Typography>
              ))}
            {messages !== null && messages.length === 0 && turn === null && (
              <Typography sx={{ fontSize: 14, color: tokens.text2 }}>
                {shared
                  ? t('assistant.shared.emptyTranscript')
                  : support
                    ? t('support.emptyTranscript')
                    : t('assistant.emptyTranscript')}
              </Typography>
            )}
            {messages?.map((message) =>
              message.role === 'USER'
                ? humanMessage(message)
                : message.role === 'SCOPE_REQUEST'
                  ? scopeLine(message)
                  : assistantLine(message),
            )}
            {conversation.newer.hasNewer && (
              <Box data-newer-lines="">
                <Button size="small" onClick={loadNewer} aria-busy={conversation.newer.loading}>
                  {conversation.newer.loading
                    ? t('assistant.history.loadingNewer')
                    : t('assistant.history.newer')}
                </Button>
                {conversation.newer.error !== null && (
                  <Alert severity="warning" sx={{ mt: 1 }} data-newer-failed="">
                    {errorCopy(t, conversation.newer.error, 'assistant.history.newerError')}
                  </Alert>
                )}
              </Box>
            )}
            {Object.values(conversation.turnDrafts)
              .filter((seen) => !placedDrafts.has(seen.turnId) && !answered.has(seen.turnId))
              .map((seen) => draftFor(seen.turnId))}
            {(conversation.running || othersThinking) && (
              <Box
                role="status"
                aria-live="polite"
                data-thinking=""
                sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}
              >
                {assistantAvatar}
                <ThinkingDots />
                <Box component="span" sx={visuallyHidden}>
                  {t('assistant.thinking')}
                </Box>
                {!conversation.running && othersThinking && (
                  <Typography
                    component="span"
                    sx={{ fontSize: 12, color: label }}
                    data-thinking-asker=""
                  >
                    {t('assistant.shared.othersThinking', {
                      name: personName(open?.askedByName ?? null, t),
                    })}
                  </Typography>
                )}
              </Box>
            )}
            {conversation.pollFailure !== null && (
              <Alert
                severity="warning"
                data-poll-failed=""
                action={
                  <Button color="inherit" size="small" onClick={conversation.retryPoll}>
                    {t('common.retry')}
                  </Button>
                }
              >
                {errorCopy(t, conversation.pollFailure.error, 'assistant.error.pollFailed')}
              </Alert>
            )}
            {turnData?.status === 'FAILED' && (
              <TurnFailure
                turn={turnData}
                onTryAgain={readOnly ? undefined : () => retryTurn(turnData.id)}
                payerName={session.paidBy?.name ?? null}
                memberLimitText={memberLimit}
              />
            )}
          </LazyRootContext.Provider>
        </Box>

        <Box
          // Attached files and notices never push the transcript out: past this, the composer scrolls.
          sx={{
            px: { xs: 2, md: 3.5 },
            pt: 1.75,
            pb: 2.75,
            borderTop: `1px solid ${tokens.line}`,
            maxHeight: '60%',
            overflowY: 'auto',
            flexShrink: 0,
          }}
        >
          {session.status === 'ARCHIVED' ? (
            <Alert
              severity="info"
              data-archived-notice=""
              action={
                session.canManage ? (
                  <Button color="inherit" size="small" onClick={onRestore}>
                    {t('assistant.menu.restore')}
                  </Button>
                ) : undefined
              }
            >
              {session.canManage
                ? t('assistant.archivedNotice')
                : t('assistant.shared.archivedNotice')}
            </Alert>
          ) : roomClosed && !parentSettled ? (
            // Until the shared session's read settles, a read-only room shows neither the reason nor
            // Delete: a paused room is never offered Delete (TMMAPP-0166 item 1). A failed read shows
            // its error and Retry instead of a blank composer (TMMAPP-0167 item 1).
            parentFailure !== null ? (
              <ErrorState
                variant="section"
                error={parentFailure.error}
                what={t('errors.what.sharedSession')}
                onRetry={parentFailure.onRetry}
              />
            ) : null
          ) : roomCantAsk && parent !== null ? (
            <CantAskNotice
              reason={parent.askReason}
              virtualAccountName={virtualAccountName}
              ownerName={parent.ownerName}
            />
          ) : roomClosed ? (
            <Alert
              severity="info"
              data-room-closed=""
              action={
                onDelete !== undefined ? (
                  <Button color="inherit" size="small" onClick={onDelete}>
                    {t('assistant.breakout.delete')}
                  </Button>
                ) : undefined
              }
            >
              {t('assistant.breakout.readOnly')}
            </Alert>
          ) : session.accessEnded ? (
            <Alert severity="info" data-access-ended="">
              {t('assistant.accessEnded')}
            </Alert>
          ) : shared ? (
            <Composer
              label={t('assistant.composer.label')}
              placeholder={placeholder}
              sendLabel={t('assistant.composer.sendMembers')}
              sendHeld={false}
              onSend={post}
              ask={{
                label: t('assistant.composer.sendAssistant'),
                shortLabel: t('assistant.composer.sendAssistantShort'),
                held: conversation.running,
                onAsk: ask,
                // Never disabled: a member who can't ask is told why (item 14).
                explain: callerCanAsk
                  ? undefined
                  : () => {
                      setSendError(null);
                      setCantAsk({ reason: callerReason });
                    },
              }}
              notice={notice}
              controls={tierControl}
              hint={composerHint}
              attachments={attachments}
              initialText={initialText}
              chips={chips}
            />
          ) : (
            <Composer
              label={t('assistant.composer.label')}
              placeholder={placeholder}
              sendLabel={t('assistant.composer.send')}
              sendHeld={conversation.running}
              onSend={ask}
              notice={notice}
              controls={tierControl}
              hint={
                support && !subscriber ? (
                  <Typography sx={{ fontSize: 12, color: label }} data-composer-hint="">
                    {t('support.askNeedsSubscription')}
                  </Typography>
                ) : undefined
              }
              attachments={attachments}
              initialText={initialText}
              chips={chips}
            />
          )}
          <Typography
            component="p"
            sx={{ fontSize: 12, color: label, mt: 1, mb: 0 }}
            data-disclaimer=""
          >
            {t('assistant.disclaimer')}
          </Typography>
        </Box>
        <LiveRegion key={announcement.key} text={announcement.text} />
        {bringBack !== null && onShareBack !== undefined && (
          <BringBackDialog
            sharedName={sharedName}
            initialNote={bringBack.note}
            writable={parent?.writable ?? true}
            onClose={() => setBringBack(null)}
            onShare={async (note) => {
              await onShareBack(note);
              setBringBack(null);
            }}
          />
        )}
      </Stack>
    </Stack>
  );
}

/** Which prompt chips each kind of session offers (catalog keys, from the prototype's chips). */
const CHIPS: Record<SessionKind, readonly string[]> = {
  shared: ['assistant.chips.numbers', 'assistant.chips.park'],
  breakout: ['assistant.chips.gently', 'assistant.chips.numbers'],
  private: ['assistant.chips.numbers', 'assistant.chips.thinkThrough'],
  support: ['assistant.chips.connectBank', 'assistant.chips.trialEnds', 'assistant.chips.feedback'],
};

/** The dots rise in turn: motion is translate only (TMM-T4 1.4: no opacity animation). */
const THINKING_KEYFRAMES = {
  '@keyframes tmmThinkingDot': {
    '0%, 60%, 100%': { transform: 'translateY(0)' },
    '30%': { transform: 'translateY(-4px)' },
  },
};

/** "Thinking…" as three 7 px dots pulsing in turn; static with reduced motion (contract §11). */
function ThinkingDots() {
  const tokens = resolveTokens(useTheme());
  return (
    <Box
      component="span"
      aria-hidden
      sx={{ display: 'flex', gap: 0.5, ...THINKING_KEYFRAMES }}
      data-thinking-dots=""
    >
      {[0, 1, 2].map((index) => (
        <Box
          key={index}
          component="span"
          sx={{
            width: 7,
            height: 7,
            borderRadius: '50%',
            bgcolor: tokens.brandBoundary,
            animation: `tmmThinkingDot 1.2s ease-in-out ${index * 0.2}s infinite`,
            '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
          }}
        />
      ))}
    </Box>
  );
}
